import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash, UserCircle, Key, PencilSimple, Shield, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import HintButton from "@/components/HintButton";

const emptyUser = { name: "", code: "", password: "", role: "operator", sector: "" };
const SECTOR_OPTIONS = ["Setor 1", "Setor 2", "Setor 3", "Setor 4"];

export default function Collaborators() {
  const [users, setUsers] = useState([]);
  const [permKeys, setPermKeys] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState(emptyUser);
  const [resetUser, setResetUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [editUser, setEditUser] = useState(null); // {id, name, code, role}
  const [permUser, setPermUser] = useState(null); // { user, perms: {key: bool} }
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [{ data: list }, { data: perms }] = await Promise.all([
      api.get("/collaborators"),
      api.get("/permissions/available"),
    ]);
    setUsers(list);
    setPermKeys(perms.keys || []);
    setDefaults(perms.defaults || {});
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/collaborators", newUser);
      setShowAdd(false);
      setNewUser(emptyUser);
      load();
      toast.success("Colaborador cadastrado");
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro");
    }
  };

  const remove = async (id, name) => {
    if (!confirm(`Remover o colaborador "${name}"?`)) return;
    try {
      await api.delete(`/collaborators/${id}`);
      load();
      toast.success("Colaborador removido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/collaborators/${resetUser.id}/password`, { password: newPw });
      setResetUser(null);
      setNewPw("");
      toast.success("Senha alterada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/collaborators/${editUser.id}`, {
        name: editUser.name,
        code: editUser.code || "",
        role: editUser.role,
        sector: editUser.sector || "",
      });
      setEditUser(null);
      load();
      toast.success("Cadastro atualizado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  const openPermissions = (u) => {
    const currentPerms = u.permissions || {};
    const merged = {};
    permKeys.forEach((p) => {
      merged[p.key] = currentPerms[p.key] !== undefined ? !!currentPerms[p.key] : !!defaults[p.key];
    });
    setPermUser({ user: u, perms: merged });
  };

  const togglePerm = (key) => {
    setPermUser((prev) => ({ ...prev, perms: { ...prev.perms, [key]: !prev.perms[key] } }));
  };

  const savePermissions = async () => {
    setSaving(true);
    try {
      await api.put(`/collaborators/${permUser.user.id}/permissions`, permUser.perms);
      setPermUser(null);
      load();
      toast.success("Permissões atualizadas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  const selectAllPerms = (value) => {
    const all = {};
    permKeys.forEach((p) => { all[p.key] = value; });
    setPermUser((prev) => ({ ...prev, perms: all }));
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="overline text-zinc-500 mb-2">EQUIPE</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Colaboradores</h1>
          <HintButton text="Gerencie cadastros, senhas e permissões de acesso por página do sistema." />
        </div>
        <button className="btn" onClick={() => setShowAdd(true)} data-testid="add-collab">
          <Plus size={16} weight="bold" /> Adicionar
        </button>
      </div>

      <div className="border border-black bg-white overflow-hidden" data-testid="collab-list">
        <table className="table">
          <thead><tr><th>Nome</th><th>Código</th><th>Setor</th><th>Função</th><th>Permissões</th><th>Criado em</th><th className="text-right">Ações</th></tr></thead>
          <tbody>
            {users.map((u) => {
              const permCount = u.role === "admin"
                ? permKeys.length
                : Object.values(u.permissions || {}).filter(Boolean).length;
              return (
              <tr key={u.id} data-testid={`collab-row-${u.name}`}>
                <td>
                  <div className="flex items-center gap-3">
                    <UserCircle size={24} weight="bold" />
                    <span className="font-semibold">{u.name}</span>
                  </div>
                </td>
                <td>
                  <span className="font-mono text-sm bg-zinc-100 px-2 py-1" data-testid={`collab-code-${u.name}`}>{u.code || "—"}</span>
                </td>
                <td>
                  <span className="text-xs text-zinc-700" data-testid={`collab-sector-${u.name}`}>
                    {u.sector || <span className="text-zinc-400">—</span>}
                  </span>
                </td>
                <td>
                  <span className={`text-xs px-2 py-1 ${u.role === "admin" ? "bg-black text-white" : "bg-zinc-100"}`}>
                    {u.role === "admin" ? "ADMIN" : "OPERADOR"}
                  </span>
                </td>
                <td>
                  {u.role === "admin" ? (
                    <span className="text-xs font-mono text-zinc-500">todas ({permKeys.length})</span>
                  ) : (
                    <button
                      className="text-xs underline hover:text-blue-700 flex items-center gap-1"
                      onClick={() => openPermissions(u)}
                      data-testid={`collab-perms-${u.name}`}
                    >
                      <Shield size={14} weight="bold" />
                      {permCount} de {permKeys.length}
                    </button>
                  )}
                </td>
                <td className="text-xs text-zinc-500 font-mono">{new Date(u.created_at).toLocaleDateString("pt-BR")}</td>
                <td className="text-right whitespace-nowrap">
                  <button
                    className="text-xs underline mr-3 hover:text-black"
                    onClick={() => setEditUser({ id: u.id, name: u.name, code: u.code || "", role: u.role, sector: u.sector || "" })}
                    data-testid={`collab-edit-${u.name}`}
                  >
                    <PencilSimple size={12} className="inline mr-1" /> Alterar cadastro
                  </button>
                  <button className="text-xs underline mr-3 hover:text-blue-700" onClick={() => setResetUser(u)} data-testid={`collab-reset-${u.name}`}>
                    <Key size={12} className="inline mr-1" /> Trocar senha
                  </button>
                  {u.name !== "admin" && (
                    <button className="text-xs underline text-red-600" onClick={() => remove(u.id, u.name)} data-testid={`collab-remove-${u.name}`}>
                      <Trash size={12} className="inline mr-1" /> Remover
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowAdd(false)}>
          <div className="glass max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="add-collab-modal">
            <div className="overline text-zinc-500 mb-2">NOVO COLABORADOR</div>
            <h2 className="font-head text-2xl font-bold mb-4">Cadastrar</h2>
            <form onSubmit={add} className="space-y-4">
              <div>
                <label className="label">Nome (exibido em relatórios)</label>
                <input className="input" required value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="collab-name" />
              </div>
              <div>
                <label className="label">Código do Colaborador (usado na autenticação)</label>
                <input
                  className="input font-mono"
                  required
                  inputMode="numeric"
                  placeholder="ex: 101"
                  value={newUser.code}
                  onChange={(e) => setNewUser({ ...newUser, code: e.target.value })}
                  data-testid="collab-code-input"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Cada colaborador precisa de um código único. Ele digitará este código (não o nome) ao autorizar movimentações do Kanban e ajustes de estoque.</p>
              </div>
              <div>
                <label className="label">Senha</label>
                <input className="input" required value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="collab-password" />
              </div>
              <div>
                <label className="label">Função</label>
                <select className="input" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} data-testid="collab-role">
                  <option value="operator">Operador</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="label">Setor</label>
                <select className="input" value={newUser.sector} onChange={(e) => setNewUser({ ...newUser, sector: e.target.value })} data-testid="collab-sector">
                  <option value="">— sem setor —</option>
                  {SECTOR_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
                <p className="text-[11px] text-zinc-500 mt-1">Setor físico onde o colaborador atua na fábrica.</p>
              </div>
              {error && <div className="border border-[#E60000] bg-red-50 text-[#E60000] text-sm px-3 py-2">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" className="btn btn-ghost flex-1" onClick={() => setShowAdd(false)}>Cancelar</button>
                <button type="submit" className="btn flex-1" data-testid="collab-submit">Cadastrar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setEditUser(null)}>
          <div className="glass max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="edit-collab-modal">
            <div className="overline text-zinc-500 mb-2">ALTERAR CADASTRO</div>
            <h2 className="font-head text-2xl font-bold mb-4">{editUser.name}</h2>
            <form onSubmit={saveEdit} className="space-y-4">
              <div>
                <label className="label">Nome</label>
                <input className="input" required value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} data-testid="edit-collab-name" />
              </div>
              <div>
                <label className="label">Código</label>
                <input
                  className="input font-mono"
                  inputMode="numeric"
                  placeholder="ex: 101"
                  value={editUser.code}
                  onChange={(e) => setEditUser({ ...editUser, code: e.target.value })}
                  data-testid="edit-collab-code"
                />
              </div>
              <div>
                <label className="label">Função</label>
                <select className="input" value={editUser.role} onChange={(e) => setEditUser({ ...editUser, role: e.target.value })} data-testid="edit-collab-role">
                  <option value="operator">Operador</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="label">Setor</label>
                <select className="input" value={editUser.sector || ""} onChange={(e) => setEditUser({ ...editUser, sector: e.target.value })} data-testid="edit-collab-sector">
                  <option value="">— sem setor —</option>
                  {SECTOR_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" className="btn btn-ghost flex-1" onClick={() => setEditUser(null)}>Cancelar</button>
                <button type="submit" disabled={saving} className="btn flex-1" data-testid="edit-collab-submit">{saving ? "Salvando..." : "Salvar"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setResetUser(null)}>
          <div className="glass max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="overline text-zinc-500 mb-2">TROCAR SENHA</div>
            <h2 className="font-head text-2xl font-bold mb-4">{resetUser.name}</h2>
            <form onSubmit={resetPassword} className="space-y-4">
              <div>
                <label className="label">Nova Senha</label>
                <input className="input" required value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="collab-new-password" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" className="btn btn-ghost flex-1" onClick={() => setResetUser(null)}>Cancelar</button>
                <button type="submit" className="btn flex-1" data-testid="collab-reset-submit">Atualizar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {permUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setPermUser(null)}>
          <div className="glass max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="perm-modal">
            <div className="flex items-center gap-2 mb-2">
              <Shield size={18} weight="bold" />
              <div className="overline text-zinc-500">PERMISSÕES DE ACESSO</div>
            </div>
            <h2 className="font-head text-2xl font-bold mb-1">{permUser.user.name}</h2>
            <p className="text-xs text-zinc-500 mb-6">Marque as páginas que este colaborador poderá acessar. Itens desmarcados ficarão ocultos no menu.</p>

            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-mono text-zinc-500">
                {Object.values(permUser.perms).filter(Boolean).length} de {permKeys.length} marcadas
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => selectAllPerms(true)}
                  className="text-xs underline hover:text-black"
                  data-testid="perm-select-all"
                >
                  Marcar todas
                </button>
                <span className="text-zinc-300">|</span>
                <button
                  type="button"
                  onClick={() => selectAllPerms(false)}
                  className="text-xs underline hover:text-black"
                  data-testid="perm-clear-all"
                >
                  Limpar
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
              {permKeys.map((p) => {
                const checked = !!permUser.perms[p.key];
                return (
                  <label
                    key={p.key}
                    className={`flex items-center gap-3 border p-3 cursor-pointer transition-colors ${
                      checked ? "border-black bg-yellow-50" : "border-zinc-200 hover:border-black"
                    }`}
                    data-testid={`perm-row-${p.key}`}
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-black"
                      checked={checked}
                      onChange={() => togglePerm(p.key)}
                      data-testid={`perm-check-${p.key}`}
                    />
                    <span className="text-sm font-semibold flex-1">{p.label}</span>
                    {checked && <Check size={16} weight="bold" />}
                  </label>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button type="button" className="btn btn-ghost flex-1" onClick={() => setPermUser(null)}>Cancelar</button>
              <button
                type="button"
                disabled={saving}
                onClick={savePermissions}
                className="btn flex-1"
                data-testid="perm-save"
              >
                {saving ? "Salvando..." : "Salvar permissões"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
